import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-query';
import styled from 'styled-components';

import { Main as Container } from 'Components/Main';
import { FormSuccess } from 'Components/Form/Success';
import { PageLoaderForm } from 'Components/PageLoader/Form';
import { PageHelper } from 'Components/PageHelper';
import { DotsLoader } from 'Components/DotsLoader';

import { addCookie, editCookie, getCookieById } from 'services/cookies-service';
import { useAuthTenant } from 'store/auth';
import { Configuration } from './Configuration';

import { Dialog, PageHeader } from 'Components/ui';

const BLUE = 'var(--vo-celeste)';

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 30px 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const CancelBtn = styled.button`
  height: 44px;
  background: white;
  color: ${BLUE};
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover { background: var(--vo-capri-50); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SaveBtn = styled.button`
  height: 44px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  transition: background 0.15s;
  &:hover:not(:disabled) { background: var(--vo-cobalto); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const CookiesForm = () => {
  const { t } = useTranslation(['cookies']);
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const navigate = useNavigate();
  const [idDisclaimer, setIdDisclaimer] = useState(null);
  const [finished, setFinished] = useState(false);

  const { register, handleSubmit, formState: { errors }, control, getValues, setValue, reset } = useForm({
    defaultValues: { url: '', backgroundColor: '', primaryColor: '', textColor: '', positionController: '' },
  });

  const formatPayload = dados => ({
    name: dados.url.replace('.', '').replace('.', '').replace('http://', '').replace('https://', ''),
    domains: [dados.url],
    backgroundColor: dados.backgroundColor,
    positionController: dados.positionController,
    primaryColor: dados.primaryColor,
    textColor: dados.textColor,
    tenant: selectedTenant.slug,
  });

  const mutation = useMutation(
    () => id ? editCookie(id, formatPayload(getValues())) : addCookie(formatPayload(getValues())),
    {
      onError: error => {
        const errorMessage = error.response?.data?.message || error.message;
        Dialog.alert({ title: t('cookies:form.swal.errorTitle'), message: errorMessage, icon: 'error' });
      },
      onSuccess: res => {
        setIdDisclaimer(res.data.disclaimerId ?? id);
        setFinished(true);
      },
    },
  );

  const handleComplete = () => mutation.mutate();

  const handleReset = () => {
    reset();
    setFinished(false);
    setIdDisclaimer(null);
    navigate('/cookies/add');
  };

  const { isLoading, error, data: cookieData } = useQuery(['getCookie', id], async () => {
    if (!id) return null;
    const resCookie = await getCookieById(id);
    if (!resCookie.data) return null;
    return resCookie.data;
  });

  useEffect(() => {
    if (!id || !cookieData) return;
    setValue('url', cookieData.domain);
    setValue('backgroundColor', cookieData.background_color);
    setValue('primaryColor', cookieData.primary_color);
    setValue('textColor', cookieData.text_color);
    setValue('positionController', cookieData.position_controller);
  }, [id, cookieData, setValue]);

  const showForm = () => (id ? !isLoading && !error : true);

  const tourSteps = [
    { target: 'body', content: t('cookies:form.tour.step1'), disableBeacon: true, disableScrolling: true, placement: 'center' },
    { target: '[data-tour="page-title"]', content: t('cookies:form.tour.step2'), disableScrolling: true },
    { target: '[data-tour="cookie-url"]', content: t('cookies:form.tour.step3'), disableScrolling: false },
    { target: '[data-tour="cookie-position"]', content: t('cookies:form.tour.step4'), disableScrolling: false },
    { target: '[data-tour="cookie-colors"]', content: t('cookies:form.tour.step5'), disableScrolling: false },
    { target: '[data-tour="form-actions"]', content: t('cookies:form.tour.step6'), disableScrolling: false },
    { target: 'body', content: t('cookies:form.tour.step7'), disableScrolling: true, placement: 'center' },
  ];

  return (
    <Container>
      {isLoading && <PageLoaderForm />}

      {!finished && showForm() && (
        <PageWrap>
          <PageHeader
            data-tour="page-title"
            title={t('cookies:form.pageTitle')}
            segments={[
              { label: t('common:breadcrumbs.compliance') },
              { label: t('cookies:page.breadcrumbLabel'), route: '/cookies' },
              { label: id ? t('cookies:form.titleEdit') : t('cookies:form.titleAdd') },
            ]}
            actions={
              <HeaderActions data-tour="form-actions">
                <CancelBtn type="button" onClick={() => navigate('/cookies')} disabled={mutation.isLoading}>
                  {t('cookies:form.config.cancelButton')}
                </CancelBtn>
                <SaveBtn form="cookie-form" type="submit" disabled={mutation.isLoading}>
                  {mutation.isLoading ? <DotsLoader /> : t('cookies:form.config.saveButton')}
                </SaveBtn>
              </HeaderActions>
            }
          />

          <Configuration
            control={control}
            id={id}
            register={register}
            handleSubmit={handleSubmit}
            errors={errors}
            getValues={getValues}
            handleComplete={handleComplete}
            loading={mutation.isLoading}
          />
        </PageWrap>
      )}

      {finished && (
        <FormSuccess
          title={t('cookies:form.success.title')}
          labelStartAgain={t('cookies:form.success.startAgain')}
          handleStartAgain={() => handleReset()}
          labelShow={t('cookies:form.success.showDetails')}
          handleShow={() => navigate('/cookies/show/' + idDisclaimer)}
        />
      )}

      {!finished && <PageHelper steps={tourSteps} tourKey="cookies-form" />}
    </Container>
  );
};
